// --- Imports ---
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Fab from "@mui/material/Fab";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import { useGridApiRef } from "@mui/x-data-grid";
import * as React from "react";
import { useSelector } from "react-redux";
import LinearProgress from "@mui/material/LinearProgress";
import { NoRowOverlay } from "../../../table/NoRowOverlay";
import {
  getProductions,
  getProductionsByCenter,
  getExpiringProductions,
  deleteProduction,
} from "../../../../redux/actions/production";
import { getCenters } from "../../../../redux/actions/center";
import { store } from "../../../../store";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import CSVDownloader from "../../../ui/CSVDownloader";
import { getToday } from "../../../ui/utils/DateUtils";
import dayjs from "dayjs";
import {
  showErrorAlert,
  showWarningAlert,
} from "../../../ui/utils/AlertUtils";
import "dayjs/locale/en-gb";
import CustomFilter from "../../../table/CustomFilter";
import getUniqueOptions from "../../../table/getUniqueOptions";
import { useTranslation } from "react-i18next";
import LocalizedDataGrid from "../../../ui/datagrid/LocalizedDataGrid";
import { cdlOperator } from "../../../../shared/AccessHelper";
import { ProductionForm } from "./ProductionForm";
import { showSuccessToast } from "../../../../shared/ui/toast/Toast";
import { Modal } from "../../../../shared/ui/modal/Modal";
import { destroyProductions } from "../../../../services/productionService";

dayjs.locale("en-gb");

const SPACING = 50;

const emptyDateRangeFilter = {
  from: "",
  to: "",
};

const createEmptyComboFilters = () => ({
  centerNumber: "",
  batchNumber: "",
  materialName: "",
  status: "",
  productionDate: emptyDateRangeFilter,
  expirationDate: emptyDateRangeFilter,
  sendingAuthorization: emptyDateRangeFilter,
});

const dateFilterFields = [
  "productionDate",
  "expirationDate",
  "sendingAuthorization",
];

const getProductionFilterValue = (production, field) => {
  if (field === "centerNumber") {
    return production.Center ? production.Center.centerNumber : "At CDL";
  }

  if (field === "materialName") {
    return production.Material?.name || "";
  }

  return production[field];
};

const DateRangeFilter = ({
  item = {},
  applyValue,
  onApply,
  storedValue,
}) => {
  const { t } = useTranslation();
  const currentValue =
    item.value?.from || item.value?.to
      ? item.value
      : storedValue || emptyDateRangeFilter;
  const [draftValue, setDraftValue] = React.useState(currentValue);
  const draftValueRef = React.useRef(currentValue);
  const isEditingRef = React.useRef(false);
  const blurTimeoutRef = React.useRef(null);

  React.useEffect(() => {
    if (isEditingRef.current) {
      return;
    }

    setDraftValue(currentValue);
    draftValueRef.current = currentValue;
  }, [currentValue]);

  React.useEffect(
    () => () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    },
    [],
  );

  const updateValue = (field, value) => {
    const nextValue = {
      ...draftValue,
      [field]: value,
    };

    setDraftValue(nextValue);
    draftValueRef.current = nextValue;
  };

  const applyDraftValue = () => {
    isEditingRef.current = false;
    onApply?.(draftValueRef.current);
    applyValue({
      ...item,
      value: draftValueRef.current,
    });
  };

  const handleFocus = () => {
    isEditingRef.current = true;
  };

  const handleBlur = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    blurTimeoutRef.current = setTimeout(applyDraftValue, 0);
  };

  return (
    <Box sx={{ display: "flex", gap: 1, flexDirection: "column", pt: 1 }}>
      <TextField
        label={t("from")}
        type="date"
        size="small"
        value={draftValue.from || ""}
        onChange={(event) => {
          updateValue("from", event.target.value);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            applyDraftValue();
          }
        }}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label={t("to")}
        type="date"
        size="small"
        value={draftValue.to || ""}
        onChange={(event) => {
          updateValue("to", event.target.value);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            applyDraftValue();
          }
        }}
        InputLabelProps={{ shrink: true }}
      />
    </Box>
  );
};

export const ScreenProductions = (props) => {
  // --- Hooks and State ---
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const productions = useSelector((state) => state.production.productions);
  const user = useSelector((state) => state.user.user);
  const cdlUser = user.organization === "CDL";
  const admin = user.role === "admin";
  const employeeUser = cdlUser && user.role === "employee";
  const centerUser = user.organization === "Center";
  const canEditProductions = cdlOperator(user);
  const centers = useSelector((state) => state.center.centers);
  const reduxCenterId = user.centerId;
  const studyId = location.state?.studyId;
  const studyCode = location.state?.studyCode;
  const centerId = centerUser
    ? location.state?.centerId || reduxCenterId
    : location.state?.centerId;
  const expiring = location.state?.expiring;
  const isLoading = useSelector(
    (state) => state.production.isLoadingProductions,
  );
  const [formOpen, setFormOpen] = React.useState(false);
  const [selectedProduction, setSelectedProduction] = React.useState(null);
  const [sortedProductions, setSortedProductions] = React.useState([]);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [destructionModalOpen, setDestructionModalOpen] =
    React.useState(false);
  const [destructionDate, setDestructionDate] = React.useState(
    dayjs().format("YYYY-MM-DD"),
  );
  const [isDestroying, setIsDestroying] = React.useState(false);
  const centerNumber = location.state?.centerNumber;
  const [centerOptions, setCenterOptions] = React.useState([]);
  const [materialOptions, setMaterialOptions] = React.useState([]);
  const [batchOptions, setBatchOptions] = React.useState([]);
  const [statusOptions, setStatusOptions] = React.useState([]);
  const [comboFilters, setComboFilters] = React.useState(
    createEmptyComboFilters,
  );

  // --- Status Translations ---
  const statusTranslations = React.useMemo(
    () => ({
      produced: t("produced"),
      "at center": t("atCenter"),
      "attributed to patient": t("attributedToPatient"),
      "in transit": t("inTransit"),
      lost: t("lost"),
      destroyed: t("destroyed"),
    }),
    [t],
  );

  const dateFilterLabels = React.useMemo(
    () => ({
      productionDate: t("productionDate"),
      expirationDate: t("expirationDate"),
      sendingAuthorization: t("sendingAuthorization"),
    }),
    [t],
  );

  const createDateRangeFilterOperator = React.useCallback(
    (field) => ({
      label: `${t("from")} / ${t("to")}`,
      value: "dateRange",
      getApplyFilterFn: (filterItem) => {
        const from = filterItem.value?.from;
        const to = filterItem.value?.to;

        if (!from && !to) {
          return null;
        }

        return ({ value }) => {
          const rowDate = dayjs(value);

          if (!rowDate.isValid()) {
            return false;
          }

          if (from && rowDate.isBefore(dayjs(from), "day")) {
            return false;
          }

          if (to && rowDate.isAfter(dayjs(to), "day")) {
            return false;
          }

          return true;
        };
      },
      InputComponent: (props) => (
        <DateRangeFilter
          {...props}
          storedValue={comboFilters[field]}
          onApply={(value) => {
            setComboFilters((currentFilters) => ({
              ...currentFilters,
              [field]: {
                from: value.from || "",
                to: value.to || "",
              },
            }));
          }}
        />
      ),
    }),
    [comboFilters, t],
  );

  const activeFilterLabels = React.useMemo(() => {
    const labels = [];
    const addLabel = (label) => {
      if (label && !labels.includes(label)) {
        labels.push(label);
      }
    };

    if (comboFilters.centerNumber) {
      addLabel(
        comboFilters.centerNumber === "At CDL"
          ? t("atCDL")
          : comboFilters.centerNumber,
      );
    }

    if (comboFilters.batchNumber) {
      addLabel(comboFilters.batchNumber);
    }

    if (comboFilters.materialName) {
      addLabel(comboFilters.materialName);
    }

    if (comboFilters.status) {
      addLabel(statusTranslations[comboFilters.status] || comboFilters.status);
    }

    dateFilterFields.forEach((field) => {
      const range = comboFilters[field];

      if (!range.from && !range.to) {
        return;
      }

      const fromLabel = range.from
        ? moment(range.from).format("DD/MM/YYYY")
        : "...";
      const toLabel = range.to ? moment(range.to).format("DD/MM/YYYY") : "...";
      addLabel(`${dateFilterLabels[field]}: ${fromLabel} - ${toLabel}`);
    });

    return labels;
  }, [comboFilters, dateFilterLabels, statusTranslations, t]);

  const selectedRowsLabel = React.useMemo(() => {
    if (selectedRows.length === 0) {
      return "";
    }

    const count = selectedRows.length.toLocaleString();

    if (i18n.language?.startsWith("fr")) {
      return `${count} ligne${selectedRows.length > 1 ? "s" : ""} sélectionnée${
        selectedRows.length > 1 ? "s" : ""
      }`;
    }

    return `${count} row${selectedRows.length > 1 ? "s" : ""} selected`;
  }, [i18n.language, selectedRows.length]);

  const showFilterStatus =
    activeFilterLabels.length > 0 || selectedRows.length > 0;

  const handleResetFilters = React.useCallback(() => {
    setComboFilters(createEmptyComboFilters());
    apiRef.current?.setFilterModel?.({ items: [] });
  }, [apiRef]);

  const updateComboFilter = React.useCallback((field, value) => {
    setComboFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value || "",
    }));
  }, []);

  const displayedProductions = React.useMemo(() => {
    return sortedProductions.filter((production) => {
      const selectFields = [
        "centerNumber",
        "batchNumber",
        "materialName",
        "status",
      ];

      const matchesSelectFilters = selectFields.every((field) => {
        return (
          !comboFilters[field] ||
          getProductionFilterValue(production, field) === comboFilters[field]
        );
      });

      if (!matchesSelectFilters) {
        return false;
      }

      return dateFilterFields.every((field) => {
        const range = comboFilters[field];

        if (!range.from && !range.to) {
          return true;
        }

        const rowDate = dayjs(production[field]);

        if (!rowDate.isValid()) {
          return false;
        }

        if (range.from && rowDate.isBefore(dayjs(range.from), "day")) {
          return false;
        }

        if (range.to && rowDate.isAfter(dayjs(range.to), "day")) {
          return false;
        }

        return true;
      });
    });
  }, [comboFilters, sortedProductions]);

  React.useEffect(() => {
    const visibleIds = new Set(displayedProductions.map((row) => row.id));
    setSelectedRows((currentRows) =>
      currentRows.filter((rowId) => visibleIds.has(rowId)),
    );
  }, [displayedProductions]);

  // --- Effects for Options ---
  React.useEffect(() => {
    if (sortedProductions) {
      setCenterOptions(
        getUniqueOptions(
          sortedProductions,
          "Center.centerNumber",
          { "At CDL": t("atCDL") },
          "At CDL",
        ),
      );
      setMaterialOptions(getUniqueOptions(sortedProductions, "Material.name"));
      setBatchOptions(getUniqueOptions(sortedProductions, "batchNumber"));
      setStatusOptions(
        getUniqueOptions(sortedProductions, "status", statusTranslations),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedProductions]);

  // --- DataGrid Columns ---
  const columns = React.useMemo(() => {
    const baseColumns = [
      ...(canEditProductions
        ? [
            {
              field: "actions",
              headerName: t("actions"),
              width: 72,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderCell: (params) => (
                <Tooltip title={t("editProduction")}>
                  <IconButton
                    size="small"
                    aria-label={`${t("editProduction")} ${params.row.reference}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedProduction(params.row);
                      setFormOpen(true);
                    }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            },
          ]
        : []),
      {
        field: "centerNumber",
        headerName: t("center"),
        valueGetter: (params) => {
          return params.row.Center ? params.row.Center.centerNumber : "At CDL";
        },
        renderCell: (params) => {
          return params.row.Center
            ? params.row.Center.centerNumber
            : t("atCDL");
        },
        filterable: true,
        filterOperators: [
          {
            label: t("center"),
            value: "centerNumber",
            getApplyFilterFn: (filterItem) => {
              if (!filterItem.value) {
                return null;
              }
              return ({ value }) => value === filterItem.value;
            },
            InputComponent: (props) => (
              <CustomFilter
                label={t("center")}
                options={centerOptions}
                {...props}
                item={{
                  ...props.item,
                  value: props.item.value || comboFilters.centerNumber,
                }}
                applyValue={(nextItem) => {
                  props.applyValue(nextItem);
                  updateComboFilter("centerNumber", nextItem.value);
                }}
              />
            ),
          },
        ],
      },
      {
        field: "batchNumber",
        headerName: t("batch"),
        width: 80,
        filterable: true,
        filterOperators: [
          {
            label: t("batch"),
            value: "batchNumber",
            getApplyFilterFn: (filterItem) => {
              if (!filterItem.value) {
                return null;
              }
              return ({ value }) => value === filterItem.value;
            },
            InputComponent: (props) => (
              <CustomFilter
                label={t("batch")}
                options={batchOptions}
                {...props}
                item={{
                  ...props.item,
                  value: props.item.value || comboFilters.batchNumber,
                }}
                applyValue={(nextItem) => {
                  props.applyValue(nextItem);
                  updateComboFilter("batchNumber", nextItem.value);
                }}
              />
            ),
          },
        ],
      },
      { field: "reference", headerName: t("reference"), width: 130 },
      {
        field: "materialName",
        headerName: t("materialType"),
        flex: 2,
        minWidth: 250,
        valueGetter: (params) => {
          return params.row.Material.name;
        },
        filterable: true,
        filterOperators: [
          {
            label: t("materialType"),
            value: "materialName",
            getApplyFilterFn: (filterItem) => {
              if (!filterItem.value) {
                return null;
              }
              return ({ value }) => value === filterItem.value;
            },
            InputComponent: (props) => (
              <CustomFilter
                label={t("materialType")}
                options={materialOptions}
                {...props}
                item={{
                  ...props.item,
                  value: props.item.value || comboFilters.materialName,
                }}
                applyValue={(nextItem) => {
                  props.applyValue(nextItem);
                  updateComboFilter("materialName", nextItem.value);
                }}
              />
            ),
          },
        ],
      },
      {
        field: "productionDate",
        type: "date",
        headerName: t("productionDate"),
        minWidth: 150,
        headerAlign: "left",
        renderHeader: () => (
          <div style={{ whiteSpace: "normal", lineHeight: "normal" }}>
            {t("productionDate")} <br />
            <span style={{ fontSize: "x-small" }}>(DD/MM/YYYY)</span>
          </div>
        ),
        valueGetter: (params) => {
          return new Date(params.value);
        },
        renderCell: (params) => {
          return moment(params.value).format("DD/MM/YYYY");
        },
        filterOperators: [createDateRangeFilterOperator("productionDate")],
      },
      {
        field: "expirationDate",
        type: "date",
        headerName: t("expirationDate"),
        minWidth: 150,
        headerAlign: "left",
        renderHeader: () => (
          <div style={{ whiteSpace: "normal", lineHeight: "normal" }}>
            {t("expirationDate")}
            <br />
            <span style={{ fontSize: "x-small" }}>(DD/MM/YYYY)</span>
          </div>
        ),
        valueGetter: (params) => {
          if (!cdlUser && params.row.status === "attributed to patient") {
            return new Date(); // return a default date when used
          } else {
            return new Date(params.value); // converts the value to a Date object
          }
        },
        renderCell: (params) => {
          const expirationDate = moment(params.value);
          const now = moment();
          const isExpired =
            (params.row.status === "at center" ||
              params.row.status === "produced") &&
            expirationDate.isBefore(now);
          const isExpiring =
            (params.row.status === "at center" ||
              params.row.status === "produced") &&
            expirationDate.isBefore(now.add(30, "days"));
          const dateFormat = employeeUser ? "MM/DD/YYYY" : "DD/MM/YYYY";
          return (
            <div
              style={
                isExpired
                  ? { color: "red" }
                  : isExpiring
                    ? { color: "darkorange" }
                    : {}
              }>
              {expirationDate.format(dateFormat)}
            </div>
          );
        },
        filterOperators: [createDateRangeFilterOperator("expirationDate")],
      },
      {
        field: "status",
        headerName: t("status"),
        flex: 2,
        minWidth: 150,
        filterable: true,
        filterOperators: [
          {
            label: "Status",
            value: "status",
            getApplyFilterFn: (filterItem) => {
              if (!filterItem.value) {
                return null;
              }
              return ({ value }) => value === filterItem.value;
            },
            InputComponent: (props) => (
              <CustomFilter
                label={t("Status")}
                options={statusOptions}
                {...props}
                item={{
                  ...props.item,
                  value: props.item.value || comboFilters.status,
                }}
                applyValue={(nextItem) => {
                  props.applyValue(nextItem);
                  updateComboFilter("status", nextItem.value);
                }}
              />
            ),
          },
        ],
        renderCell: (params) => {
          return t(params.value);
        },
      },
      {
        field: "sendingAuthorization",
        type: "date",
        headerName: t("sendingAuthorization"),
        headerAlign: "left",
        renderHeader: () => (
          <div style={{ whiteSpace: "normal", lineHeight: "normal" }}>
            {t("sendingAuthorization")}
            <br />
            <span style={{ fontSize: "x-small" }}>(DD/MM/YYYY)</span>
          </div>
        ),
        valueGetter: (params) => {
          return new Date(params.value);
        },
        renderCell: (params) => {
          return moment(params.value).format("DD/MM/YYYY");
        },
        filterOperators: [
          createDateRangeFilterOperator("sendingAuthorization"),
        ],
      },
      {
        field: "Shipment.packingSlip",
        headerName: t("airwaybill"),
        flex: 1,
        minWidth: 100,
        valueGetter: (params) =>
          params.row.Shipment ? params.row.Shipment.packingSlip : "",
      },
      {
        field: "patientNumber",
        headerName: t("patientNumber"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "attributionDate",
        headerName: t("attributionDate"),
        headerAlign: "left",
        minWidth: 150,
        type: "date",
        renderHeader: () => (
          <div style={{ whiteSpace: "normal", lineHeight: "normal" }}>
            {t("attributionDate")} <br />
            <span style={{ fontSize: "x-small" }}> (DD/MM/YYYY)</span>
          </div>
        ),
        valueGetter: (params) => {
          return params.value ? new Date(params.value) : null;
        },
        renderCell: (params) => {
          if (params.value === null) {
            return null;
          }

          const attributionDate = moment(params.value);
          const expirationDate = moment(params.row.expirationDate);
          const isAfterExpiration = attributionDate.isAfter(expirationDate);
          return (
            <div style={isAfterExpiration ? { color: "red" } : {}}>
              {attributionDate.format("DD/MM/YYYY")}
            </div>
          );
        },
      },
      {
        field: "destructionDate",
        headerName: t("destructionDate"),
        headerAlign: "left",
        type: "date",
        minWidth: 150,
        renderHeader: () => (
          <div style={{ whiteSpace: "normal", lineHeight: "normal" }}>
            {t("destructionDate")} <br />
            <span style={{ fontSize: "x-small" }}> (DD/MM/YYYY)</span>
          </div>
        ),
        valueGetter: (params) => {
          return params.value ? new Date(params.value) : null;
        },
        renderCell: (params) => {
          if (params.value === null) {
            return null;
          }

          const destructionDate = moment(params.value);
          return <div>{destructionDate.format("DD/MM/YYYY")}</div>;
        },
      },
      ...(cdlUser
        ? [
            {
              field: "protocols",
              headerName: t("protocols"),
              flex: 1.2,
              minWidth: 130,
              valueGetter: (params) => {
                const protocols = params.row.Protocols;
                if (!protocols || protocols.length === 0) {
                  return t("all");
                }
                return protocols.map((p) => p.reference).join(", ");
              },
            },
            {
              field: "cdlComment",
              headerName: t("cdlComment"),
              flex: 1.3,
              minWidth: 350,
            },
          ]
        : []),
      {
        field: "comment",
        headerName: t("siteComment"),
        flex: 1.3,
        minWidth: 350,
      },
    ];

    let filteredColumns = baseColumns;

    if (centerId) {
      // If center, filter out the centerNumber column
      filteredColumns = filteredColumns.filter(
        (column) => column.field !== "centerNumber",
      );
    }

    if (centerUser) {
      // If centerUser, filter out columns
      filteredColumns = filteredColumns.filter(
        (column) =>
          column.field !== "batchNumber" &&
          column.field !== "productionDate" &&
          column.field !== "sendingAuthorization" &&
          column.field !== "comment" &&
          column.field !== "destructionDate" &&
          column.field !== "patientNumber" &&
          column.field !== "attributionDate" &&
          column.field !== "Shipment.packingSlip",
      );
    }
    return filteredColumns.map((column) => ({
      ...column,
      cellClassName: "my-datagrid-cell",
    }));
  }, [
    batchOptions,
    canEditProductions,
    cdlUser,
    centerId,
    centerOptions,
    centerUser,
    comboFilters,
    createDateRangeFilterOperator,
    employeeUser,
    materialOptions,
    statusOptions,
    t,
    updateComboFilter,
  ]);

  // --- CSV Download Handler ---
  const handleCSVSelectedProductions = () => {
    const selectedProductions = productions.filter((production) =>
      selectedRows.includes(production.id),
    );

    const includedColumns = cdlUser
      ? columns
          .map((column) => column.field)
          .filter((field) => field !== "actions")
      : ["reference", "expirationDate", "materialName", "status"].concat(
          centerId ? [] : ["centerNumber"],
        );
    const visibleColumns = columns.filter((column) =>
      includedColumns.includes(column.field),
    );

    const printableData = selectedProductions.map((production) =>
      visibleColumns.reduce((obj, column) => {
        if (
          (column.field === "expirationDate" ||
            column.field === "destructionDate" ||
            column.field === "attributionDate" ||
            column.field === "productionDate" ||
            column.field === "sendingAuthorization") &&
          production[column.field] != null
        ) {
          obj[column.headerName] = new Date(production[column.field]);
        } else if (
          (column.field === "expirationDate" ||
            column.field === "destructionDate" ||
            column.field === "attributionDate" ||
            column.field === "productionDate" ||
            column.field === "sendingAuthorization") &&
          production[column.field] == null
        ) {
          obj[column.headerName] = "";
        } else {
          obj[column.headerName] = column.valueGetter
            ? column.valueGetter({ row: production })
            : production[column.field];
        }
        return obj;
      }, {}),
    );

    return printableData;
  };

  const handleOpenDestructionModal = () => {
    setDestructionDate(dayjs().format("YYYY-MM-DD"));
    setDestructionModalOpen(true);
  };

  const handleCloseDestructionModal = () => {
    if (!isDestroying) {
      setDestructionModalOpen(false);
    }
  };

  const handleConfirmDestruction = async (event) => {
    event.preventDefault();

    if (!destructionDate || selectedRows.length === 0) {
      return;
    }

    setIsDestroying(true);

    try {
      await destroyProductions(
        selectedRows,
        dayjs(destructionDate).toISOString(),
      );
      showSuccessToast(t("destroyProductionsSuccess"));
      setDestructionModalOpen(false);
      setSelectedRows([]);
      setRefreshKey((current) => current + 1);
    } catch (error) {
      console.error("Error destroying productions:", error);
      showErrorAlert(t("destroyProductionsFailed"));
    } finally {
      setIsDestroying(false);
    }
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedProduction(null);
  };

  const handleDeleteProduction = async () => {
    if (!selectedProduction) {
      return;
    }

    const productionId = selectedProduction.id;
    handleCloseForm();

    try {
      const result = await showWarningAlert(t("deleteProductionWarning"));
      if (result.isConfirmed) {
        await store.dispatch(deleteProduction(productionId));
        showSuccessToast(t("deleteProductionSuccess"));
        setRefreshKey((current) => current + 1);
      }
    } catch (error) {
      console.error(error);
      showErrorAlert(t("deleteProductionFailed"));
    }
  };

  // --- Fetch Productions Effect ---
  React.useEffect(() => {
    const fetchData = async () => {
      if (expiring) {
        await store.dispatch(getExpiringProductions(centerId));
      } else if (centerId) {
        await store.dispatch(getProductionsByCenter(centerId));
      } else if (studyId) {
        await store.dispatch(getProductions(studyId));
      } else if (!centerUser) {
        // Center users are routed to /productions by default.
        // Attention: Redirecting them to /studies creates a navigation loop.
        navigate("/studies");
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId, centerId, expiring, refreshKey, centerUser]);

  React.useEffect(() => {
    if (studyId && canEditProductions) {
      store.dispatch(getCenters(studyId));
    }
  }, [canEditProductions, studyId]);

  // --- Sort Productions Effect ---
  React.useEffect(() => {
    if (productions) {
      // if not cdlUser, remove from productions all that have centerId null
      let sorted = [...productions];

      const statusOrder = {
        produced: 1,
        "at center": 2,
        "attributed to patient": 3,
        "in transit": 4,
        destroyed: 5,
      };

      sorted.sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);
      setSortedProductions(sorted);
    }
  }, [productions]);

  // --- Render ---
  return (
    <Grid item xs={12}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          flexWrap: "wrap",
          height: "100%",
          overflow: "auto",
        }}>
        {/* --- DataGrid Section --- */}
        <div
          style={{
            width: "100%",
            height: "85vh",
            cursor: "pointer",
            position: "relative",
          }}>
          <LocalizedDataGrid
            apiRef={apiRef}
            autoWidth
            slots={{
              noRowsOverlay: NoRowOverlay,
              loadingOverlay: LinearProgress,
            }}
            disableColumnResize={false}
            rows={displayedProductions}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 50 },
              },
            }}
            pageSizeOptions={[20, 50, 100]}
            rowHeight={30}
            checkboxSelection
            hideFooterSelectedRowCount
            onRowSelectionModelChange={(selection) => {
              setSelectedRows(selection);
            }}
            sx={{
              "& .MuiDataGrid-filterFormDeleteIcon": {
                alignSelf: "flex-start",
                marginTop: 0.5,
              },
              "& .my-datagrid-cell": {
                fontSize: "0.75rem",
              },
            }}
          />
          {showFilterStatus && (
            <Box
              sx={{
                position: "absolute",
                left: 16,
                bottom: 10,
                display: "flex",
                alignItems: "center",
                gap: 1,
                maxWidth: "70%",
                color: "text.secondary",
                fontSize: "0.875rem",
                zIndex: 2,
              }}>
              {activeFilterLabels.length > 0 && (
                <IconButton
                  aria-label="Reset filters"
                  onClick={handleResetFilters}
                  size="small"
                  sx={{ p: 0.25, flexShrink: 0 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                {activeFilterLabels.length > 0
                  ? `Filters: ${activeFilterLabels.join(", ")}`
                  : selectedRowsLabel}
              </Box>
              {activeFilterLabels.length > 0 && selectedRowsLabel && (
                <Box
                  component="span"
                  sx={{ color: "text.primary", flexShrink: 0 }}>
                  {selectedRowsLabel}
                </Box>
              )}
            </Box>
          )}
        </div>
        {/* --- Selected Rows Actions --- */}
        {selectedRows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              px: 2,
              py: 2,
            }}>
            <Box sx={{ width: { xs: "100%", sm: 340 } }}>
              <Button
                onClick={handleOpenDestructionModal}
                variant="contained"
                color="secondary"
                fullWidth
                sx={{ height: 56, px: 2 }}>
                {t("destroySelectedProductions")}
              </Button>
            </Box>
            <Box
              sx={{
                width: { xs: "100%", sm: 340 },
                display: "flex",
                "& > button": { width: "100%" },
              }}>
              <CSVDownloader
                data={handleCSVSelectedProductions()}
                filename={`${studyCode}${
                  centerNumber ? `_${centerNumber}` : ""
                }_productions_${getToday()}`}
                name={t("selectedProductions")}
              />
            </Box>
          </Box>
        )}
      </Paper>
      <Modal
        open={destructionModalOpen}
        onClose={handleCloseDestructionModal}
        title={t("selectDestructionDate")}
        maxWidth="xs"
        closeDisabled={isDestroying}
        contentSx={{ pb: 3 }}>
        <Box
          component="form"
          onSubmit={handleConfirmDestruction}
          sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
          <TextField
            label={t("destructionDate")}
            type="date"
            value={destructionDate}
            onChange={(event) => setDestructionDate(event.target.value)}
            inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
            disabled={isDestroying}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={isDestroying || !destructionDate}
            sx={{ minHeight: 52 }}>
            {t("confirm")}
          </Button>
        </Box>
      </Modal>
      <ProductionForm
        open={formOpen}
        production={selectedProduction}
        productions={productions}
        centers={centers}
        studyId={studyId}
        studyCode={studyCode}
        canDelete={cdlUser && admin}
        onClose={handleCloseForm}
        onSaved={() => setRefreshKey((current) => current + 1)}
        onDelete={handleDeleteProduction}
      />
      {/* --- Add Production Button --- */}
      {cdlUser && !centerId && !employeeUser && (
        <Fab
          color="primary"
          aria-label={t("createProduction")}
          onClick={() => {
            setSelectedProduction(null);
            setFormOpen(true);
          }}
          style={{ position: "absolute", right: SPACING, bottom: SPACING }}>
          <AddIcon />
        </Fab>
      )}
    </Grid>
  );
};
// --- End of ScreenProductions ---
